import { fetchScheduleEvents } from "../schedule-events"
import { fetchWorkOrderDetailProjection, fetchWorkOrderPlanningProjection } from "../work-orders"
import { fetchContactPoints } from "../customer-registry/contact-points"
import { fetchOrganizationUsersCached } from "../organizations"

type ScheduleRange = { startAtFrom: string; startAtTo: string }

const scheduleStatuses: ScheduleEventStatus[] = [
  "RESERVED",
  "ANNOUNCED",
  "CONFIRMED",
  "COMPLETED",
  "CANCELED",
]

const defaultScheduleLimit = 5000
const uuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

const isUuid = (value: string | null | undefined): value is string =>
  Boolean(value && uuidPattern.test(value))

const resolveWorkOrderOrganizationId = (
  organizationId: string,
  workOrder: WorkOrder | null,
): string | null => {
  if (isUuid(organizationId)) return organizationId

  const fallback = workOrder?.organization_id ?? null

  return isUuid(fallback) ? fallback : null
}

const loadOrganizationUsers = async (organizationId: string) => {
  const users = await fetchOrganizationUsersCached(organizationId, { limit: 500 }).catch(() => null)

  return users?.items ?? []
}

export const buildScheduleRange = (): ScheduleRange => {
  const nowTs = Date.now()
  const startRange = new Date(nowTs)
  startRange.setMonth(startRange.getMonth() - 3)
  const endRange = new Date(nowTs)
  endRange.setFullYear(endRange.getFullYear() + 1)

  return {
    startAtFrom: startRange.toISOString(),
    startAtTo: endRange.toISOString(),
  }
}

export const loadWorkOrderDetailsData = async (organizationId: string, workOrderId: string) => {
  const projection = await fetchWorkOrderDetailProjection(workOrderId)
  const workOrder = projection.work_order
  const effectiveOrganizationId = resolveWorkOrderOrganizationId(organizationId, workOrder)
  const users = effectiveOrganizationId ? await loadOrganizationUsers(effectiveOrganizationId) : []

  return {
    workOrder,
    properties: projection.properties ?? [],
    customers: projection.customers ?? [],
    propertyCustomers: projection.property_customers ?? [],
    contactPoints: projection.contact_points ?? [],
    scheduleEvents: projection.schedule_events ?? [],
    scheduleEventCommunicationPresentations:
      projection.schedule_event_communication_presentations ?? {},
    orderTypes: projection.order_types ?? [],
    users,
  }
}

export const loadWorkOrderLetterData = async (organizationId: string, workOrderId: string) => {
  const projection = await fetchWorkOrderDetailProjection(workOrderId)

  return { workOrder: projection.work_order, properties: projection.properties ?? [] }
}

export const loadContactPointsForWorkOrder = async (
  organizationId: string,
  workOrder: WorkOrder | null,
) => {
  if (!workOrder) return []

  const query: ContactPointQuery = { organizationId }

  if (workOrder.property_id) query.propertyId = workOrder.property_id

  try {
    return await fetchContactPoints(query)
  } catch (err) {
    console.error("contact_points_load_failed", err)

    return []
  }
}

export const loadScheduleEventsForOrganization = async (organizationId: string) => {
  const { startAtFrom, startAtTo } = buildScheduleRange()

  return fetchScheduleEvents({
    organizationId,
    startAtFrom,
    startAtTo,
    statusIn: scheduleStatuses,
    limit: defaultScheduleLimit,
  })
}

export const loadAppointmentPlanningData = async (organizationId: string, workOrderId: string) => {
  const projection = await fetchWorkOrderPlanningProjection(workOrderId)
  const workOrder = projection.work_order
  const effectiveOrganizationId = resolveWorkOrderOrganizationId(organizationId, workOrder)
  const users = effectiveOrganizationId ? await loadOrganizationUsers(effectiveOrganizationId) : []

  return {
    workOrder,
    properties: projection.properties ?? [],
    users,
    scheduleEvents: projection.schedule_events ?? [],
    scheduleEventCommunicationPresentations:
      projection.schedule_event_communication_presentations ?? {},
    contactOrders: projection.contact_orders ?? [],
    customers: projection.customers ?? [],
    contactPoints: projection.contact_points ?? [],
  }
}

export const loadWorkOrderCommunicationPlanningData = async (workOrderId: string) => {
  const projection = await fetchWorkOrderPlanningProjection(workOrderId)

  return {
    contactOrders: projection.contact_orders ?? [],
    scheduleEvents: projection.schedule_events ?? [],
    scheduleEventCommunicationPresentations:
      projection.schedule_event_communication_presentations ?? {},
  }
}
